import { useDropzone } from 'react-dropzone';
import { Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Heading } from 'src/components/Heading';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { SideDropActions } from 'src/components/SideDropActions';
import Table from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { can, Can, Subject } from 'src/hooks/usePermissions';
import {
  KnowledgeBaseFile,
  KnowledgeBasePayload,
} from 'src/interfaces/knowledge-base.interface';
import { StabilityLevel } from 'src/types/stability-level';
import { cn } from 'src/utils/className';
import { getErrorMessage } from 'src/utils/requestUtils';

const headers = [
  { key: 'id', label: 'ID', width: '8rem' },
  { key: 'label', label: 'Name', width: '60%' },
  { key: 'action', label: '', width: '20%', disableSorting: true },
];

const KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'text/plain': ['.txt'],
  'text/rtf': ['.rtf'],
  'application/rtf': ['.rtf'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx',
  ],
  'text/html': ['.html', '.htm'],
};

export const ManageKnowledgeBase = () => {
  const { get, post, remove } = useAuthedRequest();

  const { role } = useEnvironment();
  const { id } = useParams();

  const notification = useNotification();

  const [isFetching, setIsFetching] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [knowledgeBase, setKnowledgeBase] = useState<KnowledgeBasePayload>({
    label: '',
    description: '',
  });

  const [knowledgeBaseFiles, setKnowledgeBaseFiles] = useState<
    KnowledgeBaseFile[]
  >([]);
  const [modalDeleteOpen, setModalDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [size, setSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const openDeleteKnowledgeBaseFile = useCallback(
    (knowledgeBaseFile: KnowledgeBaseFile) => {
      setModalDeleteOpen(true);
      setSelectedId(knowledgeBaseFile.id);
    },
    [],
  );

  const rows = useMemo(
    () =>
      knowledgeBaseFiles.map((item) => ({
        id: <CopyableField value={item.id} notification={notification} />,
        label: item.label,
        action: (
          <div
            className="flex justify-center items-center"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <SideDropActions
              options={[
                {
                  label: 'Delete',
                  Icon: Trash,
                  color: 'error' as const,
                  show: can(role, 'delete', Subject.KNOWLEDGE_BASES),
                  onClick: () => openDeleteKnowledgeBaseFile(item),
                },
              ]}
            />
          </div>
        ),
      })),
    [knowledgeBaseFiles, notification, openDeleteKnowledgeBaseFile, role],
  );

  const fetchData = useCallback(async () => {
    if (!id) {
      return;
    }

    setIsFetching(true);
    try {
      const knowledgeBaseResponse = await get(`/knowledge-bases/${id}`);
      setKnowledgeBase(knowledgeBaseResponse);
      const filesResponse = await get(`/knowledge-bases/${id}/files`);
      setKnowledgeBaseFiles(filesResponse.items);
    } catch (error) {
      notification.error(
        getErrorMessage(error, 'Failed to load knowledge base files.'),
      );
    } finally {
      setIsFetching(false);
    }
  }, [get, id, notification]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!id) {
        notification.error('Knowledge base is unavailable. Please try again.');
        return;
      }

      if (!acceptedFiles.length) {
        notification.error('No files were selected to upload.');
        return;
      }

      setIsUploading(true);
      try {
        const results = await Promise.allSettled(
          acceptedFiles.map(async (file) => {
            const formData = new FormData();
            formData.append('file', file, file.name);

            await post(`/knowledge-bases/${id}/files`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }),
        );

        const hasSuccessfulUpload = results.some(
          (result) => result.status === 'fulfilled',
        );

        if (hasSuccessfulUpload) {
          await fetchData();
        }

        const failedUploads = results.filter(
          (result) => result.status === 'rejected',
        );

        if (failedUploads.length === 1) {
          notification.error(
            getErrorMessage(
              failedUploads[0].reason,
              'Failed to upload file. Please try again.',
            ),
          );
        } else if (failedUploads.length > 1) {
          notification.error(
            `${failedUploads.length} files failed to upload. Please try again.`,
          );
        }
      } finally {
        setIsUploading(false);
      }
    },
    [fetchData, id, notification, post],
  );
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDropRejected: () => {
      notification.error(
        'Unsupported file type. Use PDF, TXT, RTF, DOC, DOCX, PPT, PPTX, or HTML.',
      );
    },
    accept: KNOWLEDGE_BASE_ACCEPTED_FILE_TYPES,
    disabled: isUploading,
  });

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setSize(perPage);
    setCurrentPage(1);
  };

  const handleRemoveKnowledgeBaseFile = async () => {
    if (isDeleting) {
      return;
    }

    setIsDeleting(true);
    try {
      await remove(`/knowledge-bases/${id}/files/${selectedId}`);
      await fetchData();
      setModalDeleteOpen(false);
      setSelectedId('');
      notification.success('File deleted successfully');
    } catch {
      notification.error('Failed to delete file');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="flex-1">
      <Heading
        title={id ? `Files in ${knowledgeBase.label}` : 'New Knowledge Base'}
        stabilityLevel={StabilityLevel.Beta}
      />

      <ContentWrapper>
        <Can operation="create" subject={Subject.KNOWLEDGE_BASES}>
          <section
            {...getRootProps({
              className: cn(
                'bg-neutral-100 p-6 rounded-lg my-6 border border-neutral-200 border-dashed',
                isUploading
                  ? 'cursor-not-allowed opacity-70'
                  : 'cursor-pointer',
              ),
            })}
          >
            <input {...getInputProps()} />
            {isUploading ? (
              <div className="flex items-center justify-center">
                <Loading size="5" variant="primary" className="mr-2" />
                <span className="text-sm text-neutral-600">
                  Uploading files...
                </span>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center">
                  Drag 'n' drop some files here, or click to select files to
                  upload
                </div>
                <div className="flex items-center justify-center text-sm">
                  Supported file types: PDF, TXT, RTF, DOC, DOCX, PPT, PPTX,
                  HTML
                </div>
              </>
            )}
          </section>
        </Can>

        <Can operation="read" subject={Subject.KNOWLEDGE_BASES}>
          <Table
            headers={headers}
            rows={rows}
            totalItems={knowledgeBaseFiles.length}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onSort={() => {}}
            loading={isFetching}
            setSize={handleSizeChange}
            defaultSize={size}
          />
        </Can>
      </ContentWrapper>

      <Modal
        title="Delete File"
        isOpen={modalDeleteOpen}
        onClose={() => {
          setModalDeleteOpen(false);
          setSelectedId('');
        }}
        actionButton={
          <Button onClick={handleRemoveKnowledgeBaseFile} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Loading size="4" className="mr-2" />
                Deleting...
              </>
            ) : (
              'Delete File'
            )}
          </Button>
        }
        className="w-96"
      >
        <div className="text-center">
          Are you sure you want to delete this file?
        </div>
      </Modal>
    </div>
  );
};
